/**
 * Epic 2 — sestaví granulární merge (E2-US3/US4) z vybraných položek diffu
 * (`SyncDiffItem.selected`) tak, že ho PŘEHRAJE přes existující Epic 4 command
 * vrstvu (`schema/commands.ts`) — žádná paralelní "aplikuj diff" logika.
 * Výhoda: `apply()`/`revert()` každého commandu je už otestovaná od Epic 4/5,
 * a undo (kdyby na to někdy došlo) by fungoval identicky jako u ruční editace.
 *
 * Nová tabulka (`objectType: 'table', changeType: 'added'`) nikdy nenese
 * vlastní sloupcové/constraintové diff položky — `compareSchemaModels`
 * (Epic 3) je pro tabulku chybějící v jedné straně nerekurzuje (viz
 * `diff.test.ts`), takže se přidává jako ATOMICKÝ celý `TableModel` z
 * čerstvě naskenovaného stavu DB. Totéž symetricky pro smazání tabulky.
 *
 * Pořadí uvnitř jedné (existující, sloučené) tabulky: přidání sloupců →
 * úpravy sloupců → komentář tabulky → primární klíč → unique/check/FK/index
 * (přidání/úprava/smazání) → smazání sloupců JAKO POSLEDNÍ. Smazání sloupce
 * až nakonec zaručí, že `removeColumnFromConstraints` (uvnitř
 * `createDeleteColumnCommand`) kaskáduje nad JIŽ aktuálním PK/unique/indexem
 * té tabulky, ne nad stavem před mergem (viz `schema/mutations.ts`).
 *
 * `revert()` dat u smazaných položek (`previousPrimaryKey` apod.) se tu
 * záměrně použije stav PŘED mergem, ne přesný mezikrok — sync/merge je dle
 * `doc/epic-13-undo-redo.md` kap. 2 NEVRATNÁ operace, `revert()` těchto
 * commandů se tedy nikdy nezavolá (zásobník se po dokončení mergu vyprázdní).
 */
import type {
  CheckConstraintModel,
  ColumnModel,
  ForeignKeyModel,
  IndexModel,
  PrimaryKeyModel,
  SchemaModel,
  TableModel,
  UniqueConstraintModel,
} from '@bits2schema/shared';
import { findTableForPath, type SyncDiffItem } from '@bits2schema/shared';
import type { ColumnEditableFields, SchemaCommand } from './commands';
import {
  createAddCheckConstraintCommand,
  createAddColumnCommand,
  createAddForeignKeyCommand,
  createAddIndexCommand,
  createAddTableCommand,
  createAddUniqueConstraintCommand,
  createDeleteCheckConstraintCommand,
  createDeleteColumnCommand,
  createDeleteForeignKeyCommand,
  createDeleteIndexCommand,
  createDeleteTableCommand,
  createDeleteUniqueConstraintCommand,
  createSetPrimaryKeyCommand,
  createUpdateCheckConstraintCommand,
  createUpdateColumnCommand,
  createUpdateForeignKeyCommand,
  createUpdateIndexCommand,
  createUpdateTableCommentCommand,
  createUpdateUniqueConstraintCommand,
} from './commands';

export interface SyncMergePlan {
  /** V pořadí k přehrání přes `schemaExecute()`. */
  commands: SchemaCommand[];
  /** `model` po aplikaci všech `commands` — čistě spočtené, bez volání `execute()`. */
  mergedSchema: SchemaModel;
  /** Celé nové tabulky z DB (E2-US4 AC3 — kandidáti pro inkrementální umístění, Epic 7 E7-US3). */
  addedTables: TableModel[];
  /** Tabulky odebírané z modelu (pro varování o skupině/poznámkách a canvas úklid, E2-US4 AC4). */
  removedTables: TableModel[];
  /**
   * Sloupce mazané z JINAK zachovaných tabulek (bez sloupců tabulek, které
   * mizí celé — ty jsou už v `removedTables`) — stejný důvod jako u
   * `removedTables`: poznámky připnuté na tyhle sloupce (`schema/dialogs/
   * DeleteColumnDialog.tsx`, E4-US6) je potřeba smazat i tady.
   */
  deletedColumns: ColumnModel[];
}

function columnEditableFields(column: ColumnModel): ColumnEditableFields {
  return {
    rawType: column.rawType,
    canonicalType: column.canonicalType,
    isTypeRecognized: column.isTypeRecognized,
    nullable: column.nullable,
    defaultValue: column.defaultValue,
    comment: column.comment,
  };
}

interface NamedDelta<T> {
  add: T[];
  update: { id: string; from: T; to: T }[];
  remove: { value: T; originalIndex: number }[];
}

function emptyDelta<T>(): NamedDelta<T> {
  return { add: [], update: [], remove: [] };
}

/** Sdíleno pro unique/check/FK/index — všechny jsou "pojmenovaný seznam" se stejným add/update/remove tvarem. */
function applyNamedChange<T extends { id: string; name: string }>(
  delta: NamedDelta<T>,
  itemName: string | null,
  changeType: SyncDiffItem['changeType'],
  beforeList: T[],
  afterList: T[],
): void {
  if (changeType === 'added') {
    const found = afterList.find((candidate) => candidate.name === itemName);
    if (found) delta.add.push(found);
  } else if (changeType === 'removed') {
    const index = beforeList.findIndex((candidate) => candidate.name === itemName);
    if (index >= 0) delta.remove.push({ value: beforeList[index], originalIndex: index });
  } else {
    const before = beforeList.find((candidate) => candidate.name === itemName);
    const after = afterList.find((candidate) => candidate.name === itemName);
    if (before && after) delta.update.push({ id: before.id, from: before, to: { ...after, id: before.id } });
  }
}

interface TablePlan {
  table: TableModel;
  columnAdds: ColumnModel[];
  columnUpdates: { columnId: string; from: ColumnEditableFields; to: ColumnEditableFields }[];
  columnDeletes: { value: ColumnModel; originalIndex: number }[];
  commentUpdate?: { from: string | null; to: string | null };
  primaryKeyUpdate?: { from: PrimaryKeyModel | null; to: PrimaryKeyModel | null };
  unique: NamedDelta<UniqueConstraintModel>;
  check: NamedDelta<CheckConstraintModel>;
  foreignKey: NamedDelta<ForeignKeyModel>;
  index: NamedDelta<IndexModel>;
}

function emptyTablePlan(table: TableModel): TablePlan {
  return {
    table,
    columnAdds: [],
    columnUpdates: [],
    columnDeletes: [],
    unique: emptyDelta<UniqueConstraintModel>(),
    check: emptyDelta<CheckConstraintModel>(),
    foreignKey: emptyDelta<ForeignKeyModel>(),
    index: emptyDelta<IndexModel>(),
  };
}

export function buildSyncMergePlan(model: SchemaModel, rescanned: SchemaModel, diff: SyncDiffItem[]): SyncMergePlan {
  const addedTables: TableModel[] = [];
  const removedTables: TableModel[] = [];
  const tablePlans = new Map<string, TablePlan>();

  const planFor = (table: TableModel): TablePlan => {
    let plan = tablePlans.get(table.id);
    if (!plan) {
      plan = emptyTablePlan(table);
      tablePlans.set(table.id, plan);
    }
    return plan;
  };

  for (const item of diff) {
    if (!item.selected) continue;

    switch (item.objectType) {
      case 'table': {
        if (item.changeType === 'added') {
          const after = findTableForPath(rescanned.tables, item.path);
          if (after) addedTables.push(after.table);
        } else if (item.changeType === 'removed') {
          const before = findTableForPath(model.tables, item.path);
          if (before) removedTables.push(before.table);
        } else {
          const before = findTableForPath(model.tables, item.path);
          const after = findTableForPath(rescanned.tables, item.path);
          if (before && after) planFor(before.table).commentUpdate = { from: before.table.comment, to: after.table.comment };
        }
        break;
      }

      case 'column': {
        const before = findTableForPath(model.tables, item.path);
        const after = findTableForPath(rescanned.tables, item.path);
        if (item.changeType === 'added') {
          if (before && after?.itemName) {
            const column = after.table.columns.find((candidate) => candidate.name === after.itemName);
            if (column) planFor(before.table).columnAdds.push(column);
          }
        } else if (item.changeType === 'removed') {
          if (before?.itemName) {
            const index = before.table.columns.findIndex((candidate) => candidate.name === before.itemName);
            if (index >= 0) planFor(before.table).columnDeletes.push({ value: before.table.columns[index], originalIndex: index });
          }
        } else if (before?.itemName && after?.itemName) {
          const oldColumn = before.table.columns.find((candidate) => candidate.name === before.itemName);
          const newColumn = after.table.columns.find((candidate) => candidate.name === after.itemName);
          if (oldColumn && newColumn) {
            planFor(before.table).columnUpdates.push({
              columnId: oldColumn.id,
              from: columnEditableFields(oldColumn),
              to: columnEditableFields(newColumn),
            });
          }
        }
        break;
      }

      case 'primaryKey': {
        const before = findTableForPath(model.tables, item.path);
        if (!before) break;
        const after = findTableForPath(rescanned.tables, item.path);
        planFor(before.table).primaryKeyUpdate = { from: before.table.primaryKey, to: after?.table.primaryKey ?? null };
        break;
      }

      case 'uniqueConstraint': {
        const before = findTableForPath(model.tables, item.path);
        if (!before) break;
        const after = findTableForPath(rescanned.tables, item.path);
        applyNamedChange(
          planFor(before.table).unique,
          before.itemName,
          item.changeType,
          before.table.uniqueConstraints,
          after?.table.uniqueConstraints ?? [],
        );
        break;
      }

      case 'checkConstraint': {
        const before = findTableForPath(model.tables, item.path);
        if (!before) break;
        const after = findTableForPath(rescanned.tables, item.path);
        applyNamedChange(
          planFor(before.table).check,
          before.itemName,
          item.changeType,
          before.table.checkConstraints,
          after?.table.checkConstraints ?? [],
        );
        break;
      }

      case 'foreignKey': {
        const before = findTableForPath(model.tables, item.path);
        if (!before) break;
        const after = findTableForPath(rescanned.tables, item.path);
        applyNamedChange(
          planFor(before.table).foreignKey,
          before.itemName,
          item.changeType,
          before.table.foreignKeys,
          after?.table.foreignKeys ?? [],
        );
        break;
      }

      case 'index': {
        const before = findTableForPath(model.tables, item.path);
        if (!before) break;
        const after = findTableForPath(rescanned.tables, item.path);
        applyNamedChange(
          planFor(before.table).index,
          before.itemName,
          item.changeType,
          before.table.indexes,
          after?.table.indexes ?? [],
        );
        break;
      }

      default:
        break;
    }
  }

  const commands: SchemaCommand[] = [];
  const deletedColumns: ColumnModel[] = [];
  for (const table of addedTables) commands.push(createAddTableCommand(table));

  for (const plan of tablePlans.values()) {
    const { table } = plan;
    for (const column of plan.columnAdds) commands.push(createAddColumnCommand(table.id, column));
    for (const update of plan.columnUpdates) {
      commands.push(createUpdateColumnCommand(table.id, update.columnId, update.from, update.to));
    }
    if (plan.commentUpdate) commands.push(createUpdateTableCommentCommand(table.id, plan.commentUpdate.from, plan.commentUpdate.to));
    if (plan.primaryKeyUpdate) commands.push(createSetPrimaryKeyCommand(table.id, plan.primaryKeyUpdate.from, plan.primaryKeyUpdate.to));

    for (const constraint of plan.unique.add) commands.push(createAddUniqueConstraintCommand(table.id, constraint));
    for (const update of plan.unique.update) commands.push(createUpdateUniqueConstraintCommand(table.id, update.id, update.from, update.to));
    for (const removal of plan.unique.remove) {
      commands.push(createDeleteUniqueConstraintCommand(table.id, removal.value, removal.originalIndex));
    }

    for (const constraint of plan.check.add) commands.push(createAddCheckConstraintCommand(table.id, constraint));
    for (const update of plan.check.update) commands.push(createUpdateCheckConstraintCommand(table.id, update.id, update.from, update.to));
    for (const removal of plan.check.remove) {
      commands.push(createDeleteCheckConstraintCommand(table.id, removal.value, removal.originalIndex));
    }

    for (const foreignKey of plan.foreignKey.add) commands.push(createAddForeignKeyCommand(table.id, foreignKey));
    for (const update of plan.foreignKey.update) commands.push(createUpdateForeignKeyCommand(table.id, update.id, update.from, update.to));
    for (const removal of plan.foreignKey.remove) {
      commands.push(createDeleteForeignKeyCommand(table.id, removal.value, removal.originalIndex));
    }

    for (const index of plan.index.add) commands.push(createAddIndexCommand(table.id, index));
    for (const update of plan.index.update) commands.push(createUpdateIndexCommand(table.id, update.id, update.from, update.to));
    for (const removal of plan.index.remove) {
      commands.push(createDeleteIndexCommand(table.id, removal.value, removal.originalIndex));
    }

    // Smazání sloupců až úplně naposled, viz hlavička souboru.
    for (const removal of plan.columnDeletes) {
      commands.push(
        createDeleteColumnCommand(table.id, removal.value, removal.originalIndex, table.primaryKey, table.uniqueConstraints, table.indexes),
      );
      deletedColumns.push(removal.value);
    }
  }

  for (const table of removedTables) {
    const originalIndex = model.tables.findIndex((candidate) => candidate.id === table.id);
    commands.push(createDeleteTableCommand(table, originalIndex));
  }

  const mergedSchema = commands.reduce((acc, command) => command.apply(acc), model);

  return { commands, mergedSchema, addedTables, removedTables, deletedColumns };
}
